import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { paymentLinkQueries } from 'receivable-invoices-js/react/api/queries/fetch-payment-link/fetch-payment-link';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';

interface UseStatusPollingProps {
  queryClient: QueryClient;
  invoiceId: string;
  enabled: boolean;
}

/**
 * Polling status for payment
 * 'idle' - polling not started
 * 'polling' - currently polling
 * 'success' - payment successful
 * 'failure' - payment failed
 * 'timeout' - polling timed out / network failure
 */
export type PaymentPollingStatus = 'idle' | 'polling' | 'success' | 'failure' | 'timeout';

export function useStatusPolling({
  queryClient,
  invoiceId,
  enabled,
}: UseStatusPollingProps): PaymentPollingStatus {
  const sentry = useSentry();

  const { isLoading, isError, failureReason, data } = useQuery<PaymentPollingStatus>({
    queryKey: ['payment-link-status', invoiceId],
    queryFn: async () => {
      const paymentLink = await queryClient.fetchQuery(
        paymentLinkQueries.findItem({ resourceId: invoiceId })
      );

      if (['open', 'processing'].includes(paymentLink.status)) {
        throw new Error('Payment still pending');
      }

      if (paymentLink.status === 'paid') {
        return 'success';
      }

      return 'failure';
    },
    enabled,
    retry: 5,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // Send errors to Sentry
  if (isError && failureReason && shouldSendToSentry(failureReason)) {
    sentry.captureException(failureReason);
  }

  if (isLoading) return 'polling';
  if (isError) return 'timeout';
  if (enabled && data) return data;
  return 'idle';
}
